import { useEffect, useRef, useState } from 'react';
import { getReverseRoutes } from 'routes/api/routesApi';
import { routeRecordId } from 'routes/routeDisplayName';

export type SharingRoute = { id: string; name: string };

// Other Routes sharing a destination account. Fetched lazily for the destination
// whose panel is open (the notice only renders there), and cached per id —
// never one call per destination upfront.
export function useSharedBySite({
  token,
  selectedDestId,
  routeId,
}: {
  token: string | undefined;
  selectedDestId: string | undefined;
  routeId: string | undefined;
}) {
  const [sharedBySite, setSharedBySite] = useState<
    Record<string, SharingRoute[]>
  >({});
  const sharedFetched = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!token || !selectedDestId || sharedFetched.current.has(selectedDestId))
      return;
    sharedFetched.current.add(selectedDestId);
    // Keyed by id, so a late response is safe to store. Dropping it while the
    // id stayed latched meant the shared-account warning never appeared for
    // that destination again — the UI would then affirm that nothing else is
    // affected.
    (async () => {
      try {
        const data = await getReverseRoutes(token, selectedDestId);
        if (data === undefined) {
          // A swallowed 401 resolves undefined. Un-mark it so the warning loads
          // after logging back in, instead of staying hidden until a full reload.
          sharedFetched.current.delete(selectedDestId);
          return;
        }
        const others = (data?.results || data || [])
          .filter((c: any) => c?.id && c.id !== routeId)
          // No name fallback to the raw id: a Route the user can't
          // resolve renders as a masked count, never as an identifier.
          // Route URLs address the provider integration id, which is
          // `c.id` here; the route record still has to exist for there
          // to be a Route page at all.
          .map((c: any) => ({
            id: routeRecordId(c) ? c.id : undefined,
            name: c.provider?.name,
          }))
          .filter((c: any) => c.id);
        setSharedBySite((prev) => ({ ...prev, [selectedDestId]: others }));
      } catch (e) {
        // Allow a retry on the next visit to this panel.
        sharedFetched.current.delete(selectedDestId);
        console.error('failed to load routes sharing site', selectedDestId, e);
      }
    })();
  }, [token, selectedDestId, routeId]);

  return sharedBySite;
}
